import React, { useEffect, useState } from 'react';
import {
  Button,
  Spinner,
  Badge,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ArrowSync20Regular } from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import DataTable, { ColumnConfig } from '../components/DataTable';

type TaxKind = 'customer' | 'product';

interface TaxRegionRow {
  id: string;
  name: string;
  kind: TaxKind;
  kind_display: string;
  rate: number;
  rate_display: string;
  is_default: boolean;
  booqable_id: string | null;
}

interface SyncResult {
  success: boolean;
  created: number;
  updated: number;
  skipped_archived: number;
  purged: number;
  purged_skipped_referenced: number;
  total: number;
}

const useStyles = makeStyles({
  page: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    minHeight: 0,
    flex: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  empty: {
    ...shorthands.padding('24px'),
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
});

export default function SettingsTaxes() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'Taxes' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  const { data: regions = [], isLoading } = useQuery({
    queryKey: ['tax-regions'],
    queryFn: async () => {
      const res = await apiFetch('/tax-regions');
      if (!res.ok) throw new Error(`Failed to fetch tax regions: ${res.statusText}`);
      const json = await res.json();
      return (json.data || []) as Array<{
        id: string;
        attributes: {
          name: string;
          rate: number;
          is_default: boolean;
          booqable_id: string | null;
          kind: TaxKind;
        };
      }>;
    },
  });

  const rows: TaxRegionRow[] = regions.map((r) => ({
    id: r.id,
    name: r.attributes.name,
    kind: r.attributes.kind,
    kind_display: r.attributes.kind === 'product' ? 'Product' : 'Customer',
    rate: r.attributes.rate,
    rate_display: `${(r.attributes.rate * 100).toFixed(2)}%`,
    is_default: r.attributes.is_default,
    booqable_id: r.attributes.booqable_id,
  }));

  const columns: ColumnConfig<TaxRegionRow>[] = [
    {
      columnKey: 'name',
      label: 'Name',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {row.name}
          {row.is_default && <Badge appearance="filled" color="brand" size="small">Default</Badge>}
        </span>
      ),
    },
    { columnKey: 'kind_display', label: 'Kind' },
    { columnKey: 'rate_display', label: 'Rate' },
    { columnKey: 'booqable_id', label: 'Booqable ID' },
  ];

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await apiFetch('/tax-regions/sync', { method: 'POST' });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Sync failed: ${res.statusText}`);
      }
      setLastResult(json as SyncResult);
      await queryClient.invalidateQueries({ queryKey: ['tax-regions'] });
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Button
          appearance="primary"
          icon={syncing ? <Spinner size="tiny" /> : <ArrowSync20Regular />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from Booqable'}
        </Button>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Sync failed</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {lastResult && !error && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Sync complete</MessageBarTitle>
            {lastResult.created} created, {lastResult.updated} updated
            {lastResult.skipped_archived > 0 ? `, ${lastResult.skipped_archived} archived skipped` : ''}
            {lastResult.purged > 0 ? `, ${lastResult.purged} stale purged` : ''}
            {lastResult.purged_skipped_referenced > 0
              ? `, ${lastResult.purged_skipped_referenced} stale kept (still referenced)`
              : ''}
            .
          </MessageBarBody>
        </MessageBar>
      )}

      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          No tax profiles synced yet. Click <strong>Sync from Booqable</strong> to pull them in.
        </div>
      ) : (
        <DataTable<TaxRegionRow>
          data={rows}
          columns={columns}
          keyField="id"
          hideableColumns
          columnVisibilityStorageKey="settings_taxes"
          primaryColumnKey="name"
          showCheckboxes={false}
          defaultGroupBy="kind_display"
          defaultGroupsExpanded
          showGroupCount={false}
        />
      )}
    </div>
  );
}
